import { useState } from 'react';
import { deleteOrgFile } from '../../storage/FileSystemAdapter';
import { scanReferencesToEntity } from '../../utils/intelligence';
import {
  SIG_TYPES, SIG_MEMBERSHIP_STATUSES, AUTHORITY_STATUSES,
} from '../../utils/enums';
import Badge from '../shared/Badge';
import ConfirmDialog from '../shared/ConfirmDialog';
import { dateStatus } from '../../utils/dateStatus';
import {
  BG_CONTENT, BG_SURFACE, BG_SHELL, BG_MUTED, BG_SUBTLE,
  BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE,
  TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, TEXT_DEFAULT,
  BORDER_DEFAULT, BRAND_PRIMARY_DARK,
  STATUS_GOOD, STATUS_BAD, STATUS_WARN, SURFACE_WHITE,
  ERROR_TEXT, ERROR_BG, ERROR_BORDER,
  FONT_BODY, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_LIST_ITEM,
} from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    loading:            'Cargando grupos de interés…',
    emptyTitle:         'No hay grupos de interés registrados.',
    emptyHint:          'Crea el primer grupo con el botón "+ Nuevo grupo".',
    colName:            'Nombre',
    colType:            'Tipo',
    colCountry:         'País',
    colPurposes:        '# Propósitos',
    colMembership:      'Membresía',
    colLastContact:     'Último contacto',
    colStatus:          'Estado',
    colActions:         'Acciones',
    filterAll:          'Todos',
    filterType:         'Tipo',
    filterCountry:      'País',
    filterMembership:   'Membresía',
    filterStatus:       'Estado',
    includeArchived:    'Incluir archivados',
    editBtn:            'Editar',
    deleteBtn:          'Eliminar',
    deleteConfirm:      '¿Eliminar el grupo "{name}"? Esta acción no se puede deshacer.',
    deleteError:        'Error al eliminar.',
    formCancel:         'Cancelar',
    badgeOverdue:       'Vencido',
    badgeSoon:          'Vence pronto',
    notSet:             '—',
    typeLabels: {
      'Professional association':           'Asociación profesional',
      'Standards body':                     'Organismo de normalización',
      'Industry consortium':                'Consorcio sectorial',
      'Threat intelligence sharing group':  'Grupo de intercambio de inteligencia de amenazas',
      'Sector ISAC':                        'ISAC sectorial',
      'Vendor community':                   'Comunidad de proveedor',
      'Open-source community':              'Comunidad open-source',
      'Academic research community':        'Comunidad académica de investigación',
      'Government-led forum':               'Foro gubernamental',
      'Other':                              'Otro',
    },
    membershipLabels: {
      'None':              'Sin membresía',
      'Free or community': 'Gratuita o comunitaria',
      'Paid individual':   'Pago individual',
      'Paid corporate':    'Pago corporativo',
      'Sponsor':           'Patrocinador',
    },
    statusLabels: {
      'Active':   'Activo',
      'Inactive': 'Inactivo',
    },
  },
  en: {
    loading:            'Loading special interest groups…',
    emptyTitle:         'No special interest groups recorded yet.',
    emptyHint:          'Create the first group using the "+ New group" button.',
    colName:            'Name',
    colType:            'Type',
    colCountry:         'Country',
    colPurposes:        '# Purposes',
    colMembership:      'Membership',
    colLastContact:     'Last contact',
    colStatus:          'Status',
    colActions:         'Actions',
    filterAll:          'All',
    filterType:         'Type',
    filterCountry:      'Country',
    filterMembership:   'Membership',
    filterStatus:       'Status',
    includeArchived:    'Include archived',
    editBtn:            'Edit',
    deleteBtn:          'Delete',
    deleteConfirm:      'Delete group "{name}"? This cannot be undone.',
    deleteError:        'Delete failed.',
    formCancel:         'Cancel',
    badgeOverdue:       'Overdue',
    badgeSoon:          'Due soon',
    notSet:             '—',
    typeLabels: {
      'Professional association':           'Professional association',
      'Standards body':                     'Standards body',
      'Industry consortium':                'Industry consortium',
      'Threat intelligence sharing group':  'Threat intelligence sharing group',
      'Sector ISAC':                        'Sector ISAC',
      'Vendor community':                   'Vendor community',
      'Open-source community':              'Open-source community',
      'Academic research community':        'Academic research community',
      'Government-led forum':               'Government-led forum',
      'Other':                              'Other',
    },
    membershipLabels: {
      'None':              'None',
      'Free or community': 'Free or community',
      'Paid individual':   'Paid individual',
      'Paid corporate':    'Paid corporate',
      'Sponsor':           'Sponsor',
    },
    statusLabels: {
      'Active':   'Active',
      'Inactive': 'Inactive',
    },
  },
};

// ── COLOR MAPS ────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'Active':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'Inactive': { bg: BG_MUTED,       text: TEXT_MUTED },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function SpecialInterestGroupList({ groups, loading, org, lang, onEdit, onGroupsChanged }) {
  const t = T[lang] || T.en;

  const [filterType,       setFilterType]       = useState('');
  const [filterCountry,    setFilterCountry]    = useState('');
  const [filterMembership, setFilterMembership] = useState('');
  const [filterStatus,     setFilterStatus]     = useState('');
  const [includeArchived,  setIncludeArchived]  = useState(false);
  const [confirmState,     setConfirmState]     = useState(null);
  const [scanningDelete,   setScanningDelete]   = useState(false);
  const [listError,        setListError]        = useState('');

  const safeGroups = Array.isArray(groups) ? groups : [];

  // §11 rule 32 — soft-delete: Inactive records are archived. Hidden by default.
  const visibleGroups = safeGroups.filter((g) => includeArchived ? true : g.status !== 'Inactive');

  const countriesInData = Array.from(new Set(visibleGroups.map((g) => g.country).filter(Boolean))).sort();

  const filtered = visibleGroups.filter((g) => {
    if (filterType       && g.type             !== filterType)       return false;
    if (filterCountry    && g.country          !== filterCountry)    return false;
    if (filterMembership && g.membershipStatus !== filterMembership) return false;
    if (filterStatus     && g.status           !== filterStatus)     return false;
    return true;
  });

  const showError = (msg) => { setListError(msg); setTimeout(() => setListError(''), 4000); };

  const handleDeleteOne = async (group) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'specialInterestGroup', group.id); }
    catch { hits = []; }
    setScanningDelete(false);
    // v1: hits is always [] — case 'specialInterestGroup' returns [] for symmetry.
    // The non-empty branch is preserved for future cross-entity references.
    if (hits.length === 0) {
      const msg = t.deleteConfirm.replace('{name}', group.shortName || group.name || group.ref || group.id);
      setConfirmState({
        message: msg,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `special-interest-groups/${group.id}.json`);
            onGroupsChanged?.();
          } catch { showError(t.deleteError); }
        },
      });
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const thSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '0.4rem 0.75rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
    fontWeight: 600, textAlign: 'left', background: BG_CONTENT, whiteSpace: 'nowrap',
  };
  const tdSt = {
    ...FONT_LIST_ITEM,
    padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'middle',
  };
  const filterSelectSt = {
    ...FONT_SMALL,
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5,
    padding: '0.3rem 0.5rem', background: SURFACE_WHITE, color: TEXT_DEFAULT, cursor: 'pointer',
  };
  const btnSmall = {
    ...BTN_SECONDARY, ...FONT_TINY,
    borderRadius: 5, padding: '0.2rem 0.5rem', cursor: 'pointer',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {listError && (
        <div style={{ ...FONT_LABEL, marginBottom: '0.75rem', padding: '0.6rem 1rem', background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8, color: ERROR_TEXT }}>
          {listError}
        </div>
      )}

      {/* Filter bar */}
      {safeGroups.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterType}</span>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {SIG_TYPES.map((v) => <option key={v} value={v}>{t.typeLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterCountry}</span>
            <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {countriesInData.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterMembership}</span>
            <select value={filterMembership} onChange={(e) => setFilterMembership(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {SIG_MEMBERSHIP_STATUSES.map((v) => <option key={v} value={v}>{t.membershipLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {AUTHORITY_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <label style={{ ...FONT_LABEL, color: TEXT_MUTED, display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.includeArchived}
          </label>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>{t.loading}</p>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.emptyHint}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, minWidth: 200 }}>{t.colName}</th>
                <th style={thSt}>{t.colType}</th>
                <th style={thSt}>{t.colCountry}</th>
                <th style={{ ...thSt, textAlign: 'center' }}>{t.colPurposes}</th>
                <th style={thSt}>{t.colMembership}</th>
                <th style={{ ...thSt, minWidth: 130 }}>{t.colLastContact}</th>
                <th style={thSt}>{t.colStatus}</th>
                <th style={thSt}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g, idx) => {
                const primary = g.shortName || g.name || g.ref || g.id;
                const secondary = g.shortName && g.name && g.shortName !== g.name ? g.name : '';
                const purposeCount = Array.isArray(g.purpose) ? g.purpose.length : 0;
                const ds = g.lastContactDate ? dateStatus(g.lastContactDate) : null;
                const membership = g.membershipStatus || 'None';
                return (
                  <tr key={g.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                    <td style={tdSt}>
                      <div style={{ fontWeight: 600, color: TEXT_HEADING }}>{primary}</div>
                      {secondary && (
                        <div style={{ ...FONT_TINY, color: TEXT_MUTED }}>{secondary}</div>
                      )}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>
                      {t.typeLabels[g.type] || g.type || t.notSet}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>{g.country || t.notSet}</td>
                    <td style={{ ...tdSt, textAlign: 'center', ...FONT_LABEL, color: TEXT_DEFAULT }}>
                      {purposeCount}
                    </td>
                    <td style={tdSt}>
                      {membership !== 'None' ? (
                        <span style={{
                          ...FONT_TINY,
                          fontWeight: 600,
                          background: BG_SUBTLE,
                          color: TEXT_DEFAULT,
                          border: `1px solid ${BORDER_DEFAULT}`,
                          borderRadius: 4,
                          padding: '0.15rem 0.45rem',
                          whiteSpace: 'nowrap',
                        }}>
                          {t.membershipLabels[membership] || membership}
                        </span>
                      ) : (
                        <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>{t.membershipLabels['None']}</span>
                      )}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_DEFAULT }}>
                      {(() => {
                        if (!g.lastContactDate) return <span style={{ color: TEXT_MUTED }}>{t.notSet}</span>;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <span style={{ whiteSpace: 'nowrap' }}>{g.lastContactDate.slice(0, 10)}</span>
                            {ds && (
                              <span style={{
                                ...FONT_TINY,
                                fontWeight: 600,
                                background: ds === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                                color:      ds === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                                borderRadius: 4,
                                padding: '0.15rem 0.45rem',
                                whiteSpace: 'nowrap',
                              }}>
                                {ds === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={tdSt}>
                      <Badge label={g.status} displayLabel={t.statusLabels[g.status] || g.status} colorMap={STATUS_COLORS} />
                    </td>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => onEdit(g)} style={btnSmall}>{t.editBtn}</button>
                        <button
                          onClick={() => handleDeleteOne(g)}
                          disabled={scanningDelete}
                          style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}
                        >
                          {t.deleteBtn}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
      />
    </div>
  );
}
